// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Parachute provider portal. Opened in a new tab from the "Go to Parachute"
 * action on a submit-dme-order Task. There is no Parachute API integration;
 * this is just the web destination where the provider places the order.
 *
 * TODO(SLE-221): confirm the real portal URL with ops before release.
 */
export const PARACHUTE_PORTAL_URL = 'https://app.parachutehealth.com';

/** Communication.category coding for the audit note posted when a DME order is submitted. */
export const DME_COMMUNICATION_CATEGORY_SYSTEM = 'http://polarissleep.com/communication-types';
export const DME_ORDER_SUBMITTED_CATEGORY_CODE = 'dme-order-submitted';
export const DME_ORDER_SUBMITTED_CATEGORY_DISPLAY = 'DME Order Submitted';

/** Task.output type label used to store the Parachute order reference number. */
export const DME_ORDER_REFERENCE_OUTPUT_LABEL = 'Parachute order reference number';

export const DME_TASK_BUSINESS_STATUS_URL = 'https://polarissleep.com/fhir/StructureDefinition/task-business-status';
export const DME_TASK_BUSINESS_STATUSES = {
  new: 'New',
  pendingDocumentation: 'Pending documentation',
  claimed: 'Claimed',
  submitted: 'Submitted to vendor',
  acknowledged: 'Confirmed by vendor',
  blocked: 'Needs clinician input',
  closed: 'Closed',
} as const;

export type DmeTaskBusinessStatus = (typeof DME_TASK_BUSINESS_STATUSES)[keyof typeof DME_TASK_BUSINESS_STATUSES];

export function getDmeTaskBusinessStatus(task: { extension?: { url?: string; valueCode?: string }[] }): string {
  return (
    task.extension?.find((extension) => extension.url === DME_TASK_BUSINESS_STATUS_URL)?.valueCode ??
    DME_TASK_BUSINESS_STATUSES.new
  );
}

export function withDmeTaskBusinessStatus<T extends { extension?: { url?: string; valueCode?: string }[] }>(
  task: T,
  status: DmeTaskBusinessStatus
): T {
  return {
    ...task,
    extension: [
      ...(task.extension ?? []).filter((extension) => extension.url !== DME_TASK_BUSINESS_STATUS_URL),
      { url: DME_TASK_BUSINESS_STATUS_URL, valueCode: status },
    ],
  };
}

/** Task.statusReason coding system for a cancelled DME order. */
export const DME_CANCEL_REASON_SYSTEM = 'http://polarissleep.com/dme-cancel-reason';

/** Reasons a provider can pick when cancelling a DME order. */
export const DME_CANCEL_REASONS = [
  { code: 'patient-declined', label: 'Patient declined' },
  { code: 'insurance-issue', label: 'Insurance issue' },
  { code: 'clinical-change', label: 'Clinical change' },
] as const;

export type DmeCancelReasonCode = (typeof DME_CANCEL_REASONS)[number]['code'];
